import axios, { AxiosError, type AxiosProgressEvent } from 'axios';
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { env } from '@/lib/env';

export interface AxiosArgs {
  url: string;
  method?: string;
  body?: unknown;
  params?: Record<string, unknown>;
  headers?: FetchArgs['headers'];
  timeout?: number;
  onUploadProgress?: (event: AxiosProgressEvent) => void;
  onDownloadProgress?: (event: AxiosProgressEvent) => void;
}

export function hasProgressCallback(args: unknown): boolean {
  if (typeof args !== 'object' || args === null)
    return false;

  const { onUploadProgress, onDownloadProgress } = args as AxiosArgs;

  return onUploadProgress !== undefined || onDownloadProgress !== undefined;
}

export interface AxiosBaseQueryOptions {
  prepareHeaders?: () => Record<string, string>;
}

// same error type as `fetchBaseQuery`
export type AxiosBaseQuery = BaseQueryFn<string | AxiosArgs, unknown, FetchBaseQueryError, unknown, undefined>;

const http = axios.create({ baseURL: env.VITE_API_URL });

export function axiosBaseQuery(options: AxiosBaseQueryOptions = {}): AxiosBaseQuery {
  return async (rawArgs, api) => {
    const args = typeof rawArgs === 'string' ? { url: rawArgs } : rawArgs;

    try {
      const response = await http.request({
        url: args.url,
        method: args.method ?? 'GET',
        data: args.body,
        params: args.params,
        headers: { ...options.prepareHeaders?.(), ...toHeaderRecord(args.headers) },
        timeout: args.timeout,
        onUploadProgress: args.onUploadProgress,
        onDownloadProgress: args.onDownloadProgress,
        signal: api.signal,
      });

      return { data: response.data === '' ? undefined : response.data };
    } catch (err) {
      return { error: toQueryError(err) };
    }
  };
}

function toHeaderRecord(headers: FetchArgs['headers']): Record<string, string> {
  if (!headers)
    return {};

  if (headers instanceof Headers || Array.isArray(headers)) {
    return Object.fromEntries(headers) as Record<string, string>;
  }

  return Object.fromEntries(Object.entries(headers).filter((e): e is [string, string] => e[1] !== undefined));
}

function toQueryError(err: unknown): FetchBaseQueryError {
  if (!axios.isAxiosError(err)) {
    return { status: 'CUSTOM_ERROR', error: err instanceof Error ? err.message : String(err) };
  }

  if (err.response) {
    return { status: err.response.status, data: err.response.data };
  }

  if (err.code === AxiosError.ETIMEDOUT || err.code === AxiosError.ECONNABORTED) {
    return { status: 'TIMEOUT_ERROR', error: err.message };
  }

  return { status: 'FETCH_ERROR', error: err.message };
}

export function toPercent(event: AxiosProgressEvent): number | null {
  if (!event.total)
    return null;

  return Math.min(100, Math.round((event.loaded / event.total) * 100));
}
